/**
 * Card for an establishment.
 */
import * as React from 'react';
import { Building2, MapPin, DoorOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import {
  ESTABLISHMENT_STATUS_CONFIG,
  ESTABLISHMENT_SUIVI_COLUMNS,
  ESTABLISHMENT_TYPE_CONFIG,
} from '@/components/establishments/establishment-configs';
import {
  formatEstablishmentLocationLine,
  getEstablishmentTrackingDisplay,
} from '@/components/establishments/establishment-table-utils';
import type { Establishment } from '@/types/establishment.types';

/** Props for {@link EstablishmentCard}. */
interface EstablishmentCardProps {
  establishment: Establishment;
  onView?: (id: string) => void;
  onEdit?: (establishment: Establishment) => void;
  onDelete?: (id: string) => void;
  pageActions?: {
    canEditRow?: boolean;
    canDeleteRow?: boolean;
  };
}

/** Grid-mode card; tracking lines show actual / quota via {@link formatTrackingValue}. */
export const EstablishmentCard: React.FC<EstablishmentCardProps> = ({
  establishment,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const typeConfig =
    ESTABLISHMENT_TYPE_CONFIG[establishment.type as string] || ESTABLISHMENT_TYPE_CONFIG.college;
  const statusConfig =
    ESTABLISHMENT_STATUS_CONFIG[establishment.status] || ESTABLISHMENT_STATUS_CONFIG.active;
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;
  const classroomsCount = establishment.roomsCount ?? 0;
  const locationLine = formatEstablishmentLocationLine(
    establishment.city,
    establishment.department,
    establishment.departmentCode,
  );
  const tracking = getEstablishmentTrackingDisplay(establishment);

  return (
    <TableCard onClick={() => onView?.(establishment.id)}>
      <div className="data-card-header">
        <LabelBadge
          label={typeConfig.label}
          icon={TypeIcon}
          variant="ghost"
          color={typeConfig.badgeColor}
          size="sm"
        />
      </div>

      <div className="data-card-content">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]">
            <Building2 className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-semibold text-foreground text-base truncate">
              {establishment.name}
            </span>
            <div className="flex items-center flex-wrap gap-2 mt-0.5">
              <LabelBadge
                label={statusConfig.label}
                icon={StatusIcon}
                variant={statusConfig.badgeVariant}
                color={statusConfig.badgeColor}
                size="sm"
              />
            </div>
          </div>
        </div>

        {(establishment.city || establishment.department || establishment.departmentCode) && (
          <div className="flex items-center gap-2 min-w-0">
            <MapPin
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
            <span className="text-sm text-foreground truncate">{locationLine}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          <DoorOpen className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          <span className="text-sm text-foreground">
            {classroomsCount} {classroomsCount === 1 ? 'salle' : 'salles'}
          </span>
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">
              {ESTABLISHMENT_SUIVI_COLUMNS.students.label}
            </span>
            <span className="text-foreground text-sm tabular-nums">{tracking.students}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">
              {ESTABLISHMENT_SUIVI_COLUMNS.tutors.label}
            </span>
            <span className="text-foreground text-sm tabular-nums">{tracking.tutors}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{ESTABLISHMENT_SUIVI_COLUMNS.vsc.label}</span>
            <span className="text-foreground text-sm tabular-nums">{tracking.vsc}</span>
          </div>
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(establishment.id);
            }}
          />
          {canEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(establishment);
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(establishment.id);
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
